import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/lib/supabase/types';
import { fetchCertificateTemplateTypes } from '@/lib/certificates/templateTypes';
import { ticketTestAccess, type TicketTestAccess } from '@/lib/tests/ticketRequiresTest';

// Keeps each `.in()` URL well under PostgREST's limit.
const CHUNK = 100;

/**
 * Batch form of enrollmentRequiresTest for pages that list tests across many
 * enrollments (My tests, dashboard pending items). Returns the ids of the
 * enrollments whose ticket needs the event's tests; a test belonging to any
 * other enrollment must not be offered, because POST /api/test-attempts will
 * refuse the attempt (TEST_NOT_AVAILABLE_FOR_TICKET).
 *
 * Works with a browser or cookie-scoped client: enrolled users can read their
 * own tickets, and template types go through the security-definer RPC.
 * Throws on any lookup error so callers hide tests rather than guess.
 */
export async function enrollmentsRequiringTest(
  client: Pick<SupabaseClient<Database>, 'from' | 'rpc'>,
  enrollments: ReadonlyArray<{ id: string; ticket_id: string | null }>,
): Promise<Set<string>> {
  const access = await classifyEnrollmentsTestAccess(client, enrollments);
  return new Set(enrollments.filter((e) => access.get(e.id) === 'included').map((e) => e.id));
}

/** Per-enrollment TicketTestAccess; same reads and failure mode as above. */
export async function classifyEnrollmentsTestAccess(
  client: Pick<SupabaseClient<Database>, 'from' | 'rpc'>,
  enrollments: ReadonlyArray<{ id: string; ticket_id: string | null }>,
): Promise<Map<string, TicketTestAccess>> {
  const ticketIds = Array.from(new Set(enrollments.map((e) => e.ticket_id).filter((id): id is string => !!id)));

  const tickets = new Map<string, { allow_certificate: boolean | null; certificate_template_id: string | null }>();
  for (let i = 0; i < ticketIds.length; i += CHUNK) {
    const { data, error } = await client
      .from('tickets')
      .select('id, allow_certificate, certificate_template_id')
      .in('id', ticketIds.slice(i, i + CHUNK));
    if (error) throw new Error(`enrollmentsRequiringTest: tickets lookup failed: ${error.message}`);
    for (const t of data ?? []) tickets.set(t.id, t);
  }

  const templateTypes = await fetchCertificateTemplateTypes(
    client,
    Array.from(tickets.values()).map((t) => t.certificate_template_id),
  );

  const result = new Map<string, TicketTestAccess>();
  for (const e of enrollments) {
    const ticket = e.ticket_id ? tickets.get(e.ticket_id) : undefined;
    const gate = ticket
      ? {
          allow_certificate: ticket.allow_certificate,
          templateType: ticket.certificate_template_id ? templateTypes[ticket.certificate_template_id] ?? null : null,
        }
      : null;
    result.set(e.id, ticketTestAccess(gate));
  }
  return result;
}
